import CreateItem from "@/lib/components/admin/createItem";
import SetupSeats from "@/lib/components/admin/setupSeats";
import GuestInformation from "@/lib/components/reservation/guestInformation";
import { useCallback, useContext, useMemo, useState } from "react";
import styled from "styled-components";
import * as GraphQLClient from "@apollo/client";
import * as ReactQuery from "react-query";
import {
  AssignSeats,
  CreateAuthToken,
  CreateGuestType,
  CreateMasterUser,
  CreateSeatType,
  CreateSeats,
  CreateSeatsInput,
  CreateSpreadSheet,
  DeleteGuestType,
  DeleteSeatType,
  GetAllStages,
  GetGuestTypes,
  GetGuestsOfStage,
  GetMasterInitialItems,
  GetMasterUser,
  GetOneAheadProductionsInfo,
  GetPaymentsOfProduction,
  GetPriceList,
  GetReservationsOfStage,
  GetSeatTypes,
  GetSeatsOfStage,
  GetStaffsOfProduction,
  GetStaffsOfStage,
  SendEmailToStageGuests,
  SetPriceList,
  SetStaffsOfProduction,
  UpdatePayments,
  UpdateStage,
} from "@/graphql/documentNode";
import {
  CreateGuestTypeInput,
  CreateSeatTypeInput,
  CustomEMailInput,
  Guest,
  MasterUser,
  Payment,
  Price,
  Production,
  Seat,
  SeatType,
  SetPriceListInput,
  SetStaffsInput,
  Staff,
  Stage,
  TheaterCompany,
  UpdatePaymentsInput,
} from "@/graphql/types";
import {
  ProductionSettingCallbacks,
  StageSettingCallbacks,
} from "@/lib/components/admin";
import ResultBar from "@/lib/components/common/resultBar";
import LoadingIcon from "@/lib/components/common/loadingIcon";
import ProductionSetting from "@/lib/components/admin/productionSetting";
import { classifyDevice } from "@/lib/ts/device";
import { Authenticator } from "@aws-amplify/ui-react";
import CreateItemMenu from "@/lib/components/admin/createItemMenu";
import Button from "@/lib/components/common/button/button";
import { AuthUser, getCurrentUser, signOut } from "aws-amplify/auth";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import { TextField } from "@mui/material";
import Parallelogram from "@/lib/components/common/commonIcon/parallelogram";
import Header from "@/lib/components/common/header";



const AdminMenu = () => {
  const snackBar1 = useSnackBar({})
  const snackBar2 = useSnackBar({})
  const snackBar3 = useSnackBar({})
  const [showUserRegistration, setShowUserRegistration] = useState(false)
  const [inputUserName, setInputUserName] = useState("")

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [loadSuccess, setLoadSuccess] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);

  const [authToken, setAuthToken] = useState("");
  const [payments, setPayments] = useState([] as Payment[])


  const [cognitoUser, setCognitoUser] = useState<AuthUser | undefined>(undefined)
  const [user, setUser] = useState<MasterUser | undefined>(undefined)


  const [theaterCompanies, setTheaterCompanies] = useState<TheaterCompany[]>([])
  const [productions, setProductions] = useState<Production[]>([])
  const [stages, setStages] = useState<Stage[]>([])


  const [getMasterInitialItems] = GraphQLClient.useLazyQuery(GetMasterInitialItems, {
    onCompleted: (data) => {
      console.log(data)
      if (data.getMasterInitialItems) {
        setTheaterCompanies(data.getMasterInitialItems.theaterCompanies)
        setProductions(data.getMasterInitialItems.productions)
        setStages(data.getMasterInitialItems.stages)
      }
      snackBar3.onQuerySuccess()
    },
    onError: (err) => {
      console.log(err)
      snackBar3.onQueryError()
    }
  })

  const loadItems = useCallback(async () => {
    if (user) {
      await getMasterInitialItems({
        variables: {
          userId: user.id
        }
      })
    }
  }, [getMasterInitialItems, user])

  ReactQuery.useQuery([user], async () => {
    if (user) {
      snackBar3.loadStart()
      await loadItems()
    }
  })

  ReactQuery.useQuery([], async () => {
    const currentUser = await getCurrentUser()
    if (!currentUser.signInDetails) {
      setCognitoUser(undefined)
      await signOut()
    } else {
      setCognitoUser(currentUser)
    }
  })

  const [getMasterUser] = GraphQLClient.useLazyQuery(GetMasterUser, {
    onCompleted: (data) => {
      console.log(data.getMasterUser)
      setUser(data.getMasterUser)
      snackBar1.onQuerySuccess()
    },
    onError: (err) => {
      snackBar1.onQueryError()
    },
  })

  const [createMasterUser] = GraphQLClient.useMutation(CreateMasterUser, {
    onCompleted: (data) => {
      console.log(data.createMasterUser)
      setUser(data.createMasterUser)
      snackBar2.onMutationSuccess()
    },
    onError: (err) => {
      snackBar2.onMutationError()
    },
  })

  ReactQuery.useQuery([cognitoUser], async () => {
    if (cognitoUser && cognitoUser.signInDetails?.loginId) {
      // APIでユーザー取得
      snackBar1.loadStart()
      const result = await getMasterUser({
        variables: {
          email: cognitoUser.signInDetails.loginId,
        }
      })

      // ユーザー登録がなければ作成させる
      if (result.data.getMasterUser === null) {
        console.log("user not exists")
        setShowUserRegistration(true)
      } else {
        console.log("user found")
      }
    }
  })

  const inputUserNameError = useMemo(() => {
    if (!inputUserName) {
      return true
    } else {
      return false
    }
  }, [inputUserName])

  const registerUser = useCallback(async () => {
    snackBar2.loadStart()
    if (inputUserNameError) {
      snackBar2.onMutationError()
      return
    }
    if (!cognitoUser?.signInDetails?.loginId) {
      snackBar2.onMutationError()
      return
    }
    await createMasterUser({
      variables: {
        input: {
          name: inputUserName,
          email: cognitoUser?.signInDetails?.loginId
        }
      }
    })
    setShowUserRegistration(false)
  }, [cognitoUser?.signInDetails?.loginId, createMasterUser, inputUserName, inputUserNameError, snackBar2])

  // ユーザー登録画面
  const userRegistration = (
    <UserRegistrationStyle>
      <div className="title">
        <p>管理者情報登録</p>
      </div>
      <div className="guide">
        <p>ユーザーネームを設定してください。</p>
      </div>
      <div className="input">
        <TextField
          placeholder="管理者太郎"
          onChange={(event) => { setInputUserName(event.target.value) }}
          error={inputUserNameError}
          helperText={inputUserNameError ? "必須項目です" : ""}
        />
      </div>
      <div>
        <Button
          disabled={inputUserNameError || !cognitoUser?.signInDetails?.loginId}
          buttonType="powerful-red"
          label="登録"
          onClick={registerUser}
        />
      </div>
    </UserRegistrationStyle>
  )

  // ステージ一覧を取得
  const [getAllStages] = GraphQLClient.useLazyQuery(GetAllStages, {
    onError: () => {
      setLoadError(true);
    },
    fetchPolicy: "network-only",
  });
  const getAllStagesCallback = useCallback(
    async (productionId: number) => {
      setLoading(true);
      const response = await getAllStages({
        variables: {
          productionId,
        },
      });
      setLoading(false);
      return response.data ? (response.data.getAllStages as Stage[]) : [];
    },
    [getAllStages],
  );

  // スタッフ一覧を取得
  const [getStaffsOfProduction] = GraphQLClient.useLazyQuery(
    GetStaffsOfProduction,
    {
      onError: () => {
        setLoadError(true);
      },
      fetchPolicy: "network-only",
    },
  );
  const getStaffsOfProductionCallback = useCallback(
    async (productionId: number) => {
      setLoading(true);
      const response = await getStaffsOfProduction({
        variables: {
          productionId,
        },
      });
      setLoading(false);
      return response.data
        ? (response.data.getStaffsOfProduction as Staff[])
        : [];
    },
    [getStaffsOfProduction],
  );

  // 座席種類一覧を取得
  const [getSeatTypes] = GraphQLClient.useLazyQuery(GetSeatTypes, {
    onError: (err) => {
      setLoadError(true);
    },
    fetchPolicy: "network-only",
  });
  const getSeatTypesCallback = useCallback(
    async (stageId: number) => {
      setLoading(true);
      const response = await getSeatTypes({
        variables: {
          stageId,
        },
      });
      setLoading(false);
      return response.data ? (response.data.getSeatTypes as SeatType[]) : [];
    },
    [getSeatTypes],
  );

  // 観客種類一覧を取得
  const [getGuestTypes] = GraphQLClient.useLazyQuery(GetGuestTypes, {
    onError: (err) => {
      setLoadError(true);
    },
    fetchPolicy: "network-only",
  });
  const getGuestTypesCallback = useCallback(
    async (stageId: number) => {
      setLoading(true);
      const response = await getGuestTypes({
        variables: {
          stageId,
        },
      });
      setLoading(false);
      return response.data ? (response.data.getGuestTypes as SeatType[]) : [];
    },
    [getGuestTypes],
  );

  // 座席情報取得
  const [getSeatsOfStage] = GraphQLClient.useLazyQuery(GetSeatsOfStage, {
    onError: (err) => {
      setLoadError(true);
    },
    fetchPolicy: "network-only",
  });
  const getSeatsOfStageCallback = useCallback(
    async (stageId: number) => {
      setLoading(true);
      const response = await getSeatsOfStage({
        variables: {
          stageId,
        },
      });
      setLoading(false);
      return response.data ? (response.data.getSeatsOfStage as Seat[][]) : [];
    },
    [getSeatsOfStage],
  );

  // 価格設定を取得
  const [getPriceList] = GraphQLClient.useLazyQuery(GetPriceList, {
    onError: (err) => {
      setLoadError(true);
    },
    fetchPolicy: "network-only",
  });
  const getPriceListCallback = useCallback(
    async (stageId: number | undefined) => {
      setLoading(true);
      const response = stageId
        ? await getPriceList({
          variables: {
            stageId,
          },
        })
        : { data: undefined };
      setLoading(false);
      return response.data ? (response.data.getPriceList as Price[]) : [];
    },
    [getPriceList],
  );

  const [getStaffsOfStage] = GraphQLClient.useLazyQuery(GetStaffsOfStage, {
    fetchPolicy: "network-only",
  });

  const getStaffsCallback = useCallback(
    async (stageId: number | undefined) => {
      if (stageId === undefined) {
        return [];
      }
      const staffs = await getStaffsOfStage({
        variables: {
          stageId,
        },
      });
      return staffs.data?.getStaffsOfStage;
    },
    [getStaffsOfStage],
  );

  const [getGuestsOfStage] = GraphQLClient.useLazyQuery(GetGuestsOfStage, {
    fetchPolicy: "network-only",
  });

  const getGuestsCallback = useCallback(
    async (stageId: number | undefined) => {
      if (stageId === undefined) {
        return [];
      }
      const guests = await getGuestsOfStage({
        variables: {
          stageId,
        },
      });
      console.log({ guests })
      return guests.data?.getGuestsOfStage;
    },
    [getGuestsOfStage],
  );

  const [getReservationOfStage] = GraphQLClient.useLazyQuery(
    GetReservationsOfStage,
    {
      fetchPolicy: "network-only",
    },
  );

  const getReservationsCallback = useCallback(
    async (stageId: number | undefined) => {
      if (stageId === undefined) {
        return [];
      }

      const reservations = await getReservationOfStage({
        variables: {
          stageId,
        },
      });

      return reservations.data?.getReservationsOfStage;
    },
    [getReservationOfStage],
  );

  // mutations
  // スタッフ情報取得
  const [setStaffsOfProduction] = GraphQLClient.useMutation(
    SetStaffsOfProduction,
    {
      onError: () => {
        setSubmitError(true);
        setSubmitSuccess(false);
      },
      onCompleted: () => {
        setSubmitError(false);
        setSubmitSuccess(true);
      },
    },
  );
  const saveStaffs = useCallback(
    async (setStaffsInput: SetStaffsInput) => {
      setLoading(true);
      await setStaffsOfProduction({
        variables: {
          input: setStaffsInput,
        },
      });
      setLoading(false);
    },
    [setStaffsOfProduction],
  );

  // 価格情報保存
  const [setPriceList] = GraphQLClient.useMutation(SetPriceList, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: () => {
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });
  const setPriceListCallback = useCallback(
    async (input: SetPriceListInput) => {
      setLoading(true);
      await setPriceList({
        variables: {
          input,
        },
      });
      setLoading(false);
    },
    [setPriceList],
  );

  // 観客種類保存
  const [createGuestType] = GraphQLClient.useMutation(CreateGuestType, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: () => {
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });
  const createGuestTypeCallback = useCallback(
    async (input: CreateGuestTypeInput) => {
      setLoading(true);
      await createGuestType({
        variables: {
          input,
        },
      });
      setLoading(false);
    },
    [createGuestType],
  );

  // 観客種類削除
  const [deleteGuestType] = GraphQLClient.useMutation(DeleteGuestType, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: () => {
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });
  const deleteGuestTypeCallback = useCallback(
    async (id: number) => {
      setLoading(true);
      await deleteGuestType({
        variables: {
          id,
        },
      });
      setLoading(false);
    },
    [deleteGuestType],
  );

  // 座席種類保存
  const [createSeatType] = GraphQLClient.useMutation(CreateSeatType, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: () => {
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });
  const createSeatTypeCallback = useCallback(
    async (input: CreateSeatTypeInput) => {
      setLoading(true);
      await createSeatType({
        variables: {
          input,
        },
      });
      setLoading(false);
    },
    [createSeatType],
  );

  // 座席種類削除
  const [deleteSeatType] = GraphQLClient.useMutation(DeleteSeatType, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: () => {
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });
  const deleteSeatTypeCallback = useCallback(
    async (id: number) => {
      setLoading(true);
      await deleteSeatType({
        variables: {
          id,
        },
      });
      setLoading(false);
    },
    [deleteSeatType],
  );

  // 座席設定保存
  const [createSeats] = GraphQLClient.useMutation(CreateSeats, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: () => {
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });
  const createSeatsCallback = useCallback(
    async (input: CreateSeatsInput[][]) => {
      setLoading(true);
      await createSeats({
        variables: {
          input,
        },
      });
      setLoading(false);
    },
    [createSeats],
  );

  const [updateStage] = GraphQLClient.useMutation(UpdateStage, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: (data) => {
      if (!data.updateStage) {
        setSubmitError(true);
        setSubmitSuccess(false);
      } else {
        setSubmitError(false);
        setSubmitSuccess(true);
      }
    },
  });

  const changeSeatSelectPolicyCallback = useCallback(
    async (stage: Stage) => {
      await updateStage({
        variables: {
          input: {
            id: stage.id,
            disableSeatSelect: !stage.disableSeatSelect,
          },
        },
      });
      setLoading(false);
    },
    [updateStage],
  );

  const deleteStageCallback = useCallback(async (stage: Stage) => {
    await updateStage({
      variables: {
        input: {
          id: stage.id,
          deleted: true,
        }
      }
    })
    setLoading(false);
  }, [updateStage])

  const updateStageSettingsCallback = useCallback(async (stage: Stage) => {
    await updateStage({
      variables: {
        input: stage,
      }
    })
    setLoading(false)
  }, [updateStage])

  const [createAuthToken] = GraphQLClient.useMutation(CreateAuthToken, {
    onError: (err) => {
      console.log(err)
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: (data) => {
      console.log(data)
      setAuthToken(data.createAuthToken);
      setSubmitError(false);
      setSubmitSuccess(true);
    },
  });

  const createAuthTokenCallback = useCallback(
    async (stageId: number) => {
      await createAuthToken({ variables: { stageId } });
    },
    [createAuthToken],
  );

  const [assignSeats] = GraphQLClient.useMutation(AssignSeats, {
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    onCompleted: (data) => {
      setSubmitError(false);
      setSubmitSuccess(true);
    }
  })


  const assignSeatsCallback = useCallback(async (guests: Guest[]) => {
    await assignSeats({
      variables: {
        input: {
          guests,
        }
      }
    })
  }, [assignSeats])



  const [getPaymentsOfProduction] = GraphQLClient.useLazyQuery(GetPaymentsOfProduction, {
    onCompleted: (data) => {
      console.log(data)
      setPayments(data.getPaymentsOfProduction || [])
    },
    onError: () => {
      setSubmitError(true);
      setSubmitSuccess(false);
    },
    fetchPolicy: "network-only",
  })

  const getPaymentsOfProductionCallback = useCallback(async (productionId: number) => {
    const result = await getPaymentsOfProduction({
      variables: {
        productionId,
      }
    })
    console.log(result)
    return result.data.getPaymentsOfProduction
  }, [getPaymentsOfProduction])

  const [updatePayments] = GraphQLClient.useMutation(UpdatePayments, {
    onCompleted: (data) => {
      console.log(data)
      if (!data.updatePayments.ok) {
        setSubmitError(true);
        setSubmitSuccess(false);
      } else {
        setSubmitError(false);
        setSubmitSuccess(true);
      }
    },
    onError: (err) => {
      console.log(err)
      setSubmitError(true);
      setSubmitSuccess(false);
    },
  })

  const updatePaymentsCallback = useCallback(async (input: UpdatePaymentsInput) => {
    const payments: any[] = []
    input.payments.forEach((payment: any) => {
      const paymentCopy = { ...payment }
      if (paymentCopy.__typename) {
        delete paymentCopy.__typename
      }
      payments.push(paymentCopy)
    })

    console.log(payments)
    await updatePayments({
      variables: {
        input: {
          payments,
          productionId: input.productionId,
        },
      }
    })
  }, [updatePayments])

  const [createSpreadSheet] = GraphQLClient.useLazyQuery(CreateSpreadSheet, {
    onCompleted: (data) => {
      if (!data.createSpreadSheet.ok) {
        setSubmitError(true);
        setSubmitSuccess(false);
        setLoading(false)
      } else {
        const object = JSON.parse(data.createSpreadSheet.object)
        const blob = new Blob([Buffer.from(object.blob, "binary")], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })

        const url = window.URL.createObjectURL(blob)
        console.log(url)

        const link = document.createElement("a")
        link.setAttribute("href", url)
        link.setAttribute("download", object.fileName)
        link.click()
        link.remove()

        URL.revokeObjectURL(url)
        setSubmitError(false);
        setSubmitSuccess(true);
        setLoading(false)
      }
    },
    onError: (err) => {
      console.log(err)
      setSubmitError(true);
      setSubmitSuccess(false);
      setLoading(false)
    },
    fetchPolicy: "network-only",
  })

  const createSpreadSheetCallback = useCallback(async (stageId: number | undefined) => {
    if (stageId === undefined) {
      return undefined
    }
    setLoading(true)
    return await createSpreadSheet({
      variables: {
        stageId,
      }
    })
  }, [createSpreadSheet])

  const [sendEmailToStageGuests] = GraphQLClient.useMutation(SendEmailToStageGuests, {
    onCompleted: (data) => {
      if (!data.sendEmailToStageGuests || !data.sendEmailToStageGuests.ok) {
        setSubmitError(true);
        setSubmitSuccess(false);
        setLoading(false)
      } else {
        setSubmitError(false);
        setSubmitSuccess(true);
        setLoading(false)
      }
    },
    onError: (err) => {
      console.log(err)
      setSubmitError(true);
      setSubmitSuccess(false);
      setLoading(false)
    }
  })
  const sendEmailToStageGuestsCallback = useCallback(async (stageId: number, type: number, customEmailInput?: CustomEMailInput) => {
    await sendEmailToStageGuests({
      variables: {
        input: {
          stageId,
          type,
          customEmailInput,
        }
      }
    })
  }, [sendEmailToStageGuests])

  const stageSettingCallbacks: StageSettingCallbacks = useMemo(
    () => ({
      getAllStagesCallback,
      getSeatTypesCallback,
      getGuestTypesCallback,
      getSeatsOfStageCallback,
      getPriceListCallback,
      getGuestsCallback,
      getReservationsCallback,
      getStaffsCallback,
      setPriceListCallback,
      createGuestTypeCallback,
      createSeatsCallback,
      createSeatTypeCallback,
      deleteGuestTypeCallback,
      deleteSeatTypeCallback,
      changeSeatSelectPolicyCallback,
      createAuthTokenCallback,
      deleteStageCallback,
      updateStageSettingsCallback,
      assignSeatsCallback,
      createSpreadSheetCallback,
      sendEmailToStageGuestsCallback,
    }),
    [assignSeatsCallback, changeSeatSelectPolicyCallback, createAuthTokenCallback, createGuestTypeCallback, createSeatTypeCallback, createSeatsCallback, createSpreadSheetCallback, deleteGuestTypeCallback, deleteSeatTypeCallback, deleteStageCallback, getAllStagesCallback, getGuestTypesCallback, getGuestsCallback, getPriceListCallback, getReservationsCallback, getSeatTypesCallback, getSeatsOfStageCallback, getStaffsCallback, sendEmailToStageGuestsCallback, setPriceListCallback, updateStageSettingsCallback],
  );

  const productionSettingCallbacks: ProductionSettingCallbacks = useMemo(
    () => ({
      getStaffsOfProductionCallback,
      saveStaffs,
      getPaymentsOfProductionCallback,
      updatePaymentsCallback,
    }),
    [getPaymentsOfProductionCallback, getStaffsOfProductionCallback, saveStaffs, updatePaymentsCallback],
  );

  const executeSignOut = useCallback((signOut: any | undefined) => {
    setCognitoUser(undefined)
    setUser(undefined)
    signOut?.()
  }, [])
  const components =
    <Authenticator>
      {({ signOut, user: signInUser }) => {
        setCognitoUser(signInUser)
        console.log(cognitoUser?.signInDetails)
        return cognitoUser?.signInDetails ? (
          <div style={{ position: modalOpen ? "static" : "static" }}>
            <Header />
            <UserDataStyle>
              <div className="title">
                <Parallelogram
                  bodyColor="red"
                  shadowColor="white"
                >
                  サインイン済
                </Parallelogram>
              </div>
              <div className="user-name">
                {user?.name}
              </div>
              <div>
                <Button
                  label="サインアウト"
                  onClick={signOut ? () => { executeSignOut(signOut) } : () => { }}
                  buttonType="powerful-red-white-shadow"
                />
              </div>
            </UserDataStyle>
            {user && <CreateItemMenu
              user={user}
              theaterCompanies={theaterCompanies}
              productions={productions}
              stages={stages}
              refresh={loadItems}
            />}
            <SetupSeats
              callbacks={stageSettingCallbacks}
              productions={productions}
              authToken={authToken}
              modalChange={(open) => {
                setModalOpen(open);
              }}
            />
            <ProductionSetting
              callbacks={productionSettingCallbacks}
              productionList={productions}
              device={classifyDevice()}
              payments={payments}
            />
            <ResultBar
              setSubmitError={setLoadError}
              submitError={loadError}
              setSubmitSucceeded={setLoadSuccess}
              submitSucceeded={loadSuccess}
              succeedMsg="読み込みに成功しました"
              failedMsg="データの読み込みに失敗しました"
            />
            <ResultBar
              setSubmitError={setSubmitError}
              submitError={submitError}
              setSubmitSucceeded={setSubmitSuccess}
              submitSucceeded={submitSuccess}
              succeedMsg="保存に成功しました"
              failedMsg="保存に失敗しました"
            />
            {loading ? <LoadingIcon /> : <></>}
            {snackBar1.component}
            {snackBar2.component}
            {snackBar3.component}
          </div>) : (
          <div>
            <Button
              buttonType="powerful-red-white-shadow"
              label="サインイン "
              onClick={() => { signOut?.() }}
            />
          </div>
        )
      }}
    </Authenticator>
  return (
    <AdminMenuStyle>
      {showUserRegistration ? userRegistration : components}
    </AdminMenuStyle>
  )
};

const AdminMenuStyle = styled.div`
  width: 100%;
  background-color: black;
  margin: auto;
  text-align: center;
`;

const AdminMenuStylePC = styled.div`
  width: 100%;
  background-color: black;
  margin: auto;
  text-align: center;
`;

const UserRegistrationStyle = styled.div`
  background-color: white;
  color: black;
  width: 360px;
  border-radius: 8px;
  padding: 16px;
  margin: auto;
  .title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
  }
`
const UserDataStyle = styled.div`
  margin: 100px auto 16px auto;
  padding: 16px;
  border: 3px solid white;
  border-radius: 8px;
  width: 360px;
  .title {
    margin-bottom: 20px;
  }
  .user-name {
    margin-bottom: 8px;
  }
`

export default AdminMenu;
